import { useState, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  AreaChart, Area, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Minus, Calendar, Target, Award, Brain, Activity } from 'lucide-react';

const GRADE_COLORS = {
  5: '#10B981',
  4: '#3B82F6', 
  3: '#F59E0B',
  2: '#F97316',
  1: '#EF4444'
};

const GRADE_LABELS = {
  5: 'Excellent',
  4: 'Good',
  3: 'Average',
  2: 'Below Average',
  1: 'Needs Improvement'
};

export default function PerformanceTrends({ athleticTests, coachRatings, childName }) {
  const [selectedTest, setSelectedTest] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [viewMode, setViewMode] = useState('athletic'); // athletic, psychological, comparison

  // Get unique test types
  const testTypes = useMemo(() => {
    const types = [...new Set(athleticTests.map(t => t.test_name))];
    return types.sort();
  }, [athleticTests]);

  // Filter tests by selected type and time range
  const filteredTests = useMemo(() => {
    let tests = [...athleticTests];
    
    if (selectedTest !== 'all') {
      tests = tests.filter(t => t.test_name === selectedTest);
    }
    
    if (timeRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      if (timeRange === '3m') cutoff.setMonth(now.getMonth() - 3);
      if (timeRange === '6m') cutoff.setMonth(now.getMonth() - 6);
      if (timeRange === '1y') cutoff.setFullYear(now.getFullYear() - 1);
      tests = tests.filter(t => new Date(t.test_date) >= cutoff);
    }
    
    return tests.sort((a, b) => new Date(a.test_date) - new Date(b.test_date));
  }, [athleticTests, selectedTest, timeRange]);

  // Prepare chart data for athletic tests
  const athleticChartData = useMemo(() => {
    if (selectedTest === 'all') {
      // Group by date and show average grade
      const dateMap = {};
      filteredTests.forEach(test => {
        const date = new Date(test.test_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
        if (!dateMap[date]) {
          dateMap[date] = { grades: [], tests: [] };
        }
        dateMap[date].grades.push(test.grade);
        dateMap[date].tests.push(test.test_name);
      });
      
      return Object.entries(dateMap).map(([date, data]) => ({
        date,
        avgGrade: data.grades.reduce((a, b) => a + b, 0) / data.grades.length,
        testCount: data.tests.length,
        tests: [...new Set(data.tests)].join(', ')
      }));
    } else {
      return filteredTests.map(test => ({
        date: new Date(test.test_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }),
        value: test.result_value,
        grade: test.grade,
        unit: test.unit,
        recommendation: test.recommendation
      }));
    }
  }, [filteredTests, selectedTest]);

  // Prepare coach ratings trend data
  const ratingsChartData = useMemo(() => {
    return coachRatings
      .sort((a, b) => new Date(a.rating_date) - new Date(b.rating_date))
      .map(rating => ({
        date: new Date(rating.rating_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }),
        psychological: rating.psychological_avg,
        social: rating.social_avg,
        overall: rating.overall_avg,
        confidence: rating.confidence,
        commitment: rating.commitment,
        mentalControl: rating.mental_control,
        concentration: rating.concentration,
        enjoyingTraining: rating.enjoying_training,
        helpingOthers: rating.helping_others,
        communication: rating.communication,
        involvement: rating.involvement
      }));
  }, [coachRatings]);

  // Calculate trend (improvement/decline)
  const calculateTrend = (data, field) => {
    if (data.length < 2) return { direction: 'neutral', change: 0 };
    const recent = data.slice(-3);
    const older = data.slice(0, Math.min(3, data.length - 1));
    
    const recentAvg = recent.reduce((sum, d) => sum + (d[field] || 0), 0) / recent.length;
    const olderAvg = older.reduce((sum, d) => sum + (d[field] || 0), 0) / older.length;
    
    const change = ((recentAvg - olderAvg) / olderAvg * 100).toFixed(1);
    
    if (change > 5) return { direction: 'up', change };
    if (change < -5) return { direction: 'down', change };
    return { direction: 'neutral', change };
  };

  const athleticTrend = calculateTrend(athleticChartData, selectedTest === 'all' ? 'avgGrade' : 'grade');
  const psychTrend = calculateTrend(ratingsChartData, 'psychological');
  const socialTrend = calculateTrend(ratingsChartData, 'social');

  // Latest radar data for coach ratings
  const latestRatingRadar = useMemo(() => {
    if (coachRatings.length === 0) return [];
    const latest = coachRatings[0];
    return [
      { trait: 'Confidence', value: latest.confidence, fullMark: 10 },
      { trait: 'Commitment', value: latest.commitment, fullMark: 10 },
      { trait: 'Mental Control', value: latest.mental_control, fullMark: 10 },
      { trait: 'Concentration', value: latest.concentration, fullMark: 10 },
      { trait: 'Enjoying Training', value: latest.enjoying_training, fullMark: 10 },
      { trait: 'Helping Others', value: latest.helping_others, fullMark: 10 },
      { trait: 'Communication', value: latest.communication, fullMark: 10 },
      { trait: 'Involvement', value: latest.involvement, fullMark: 10 },
    ];
  }, [coachRatings]);

  const TrendIcon = ({ trend }) => {
    if (trend.direction === 'up') return <TrendingUp className="h-4 w-4 text-[#02b5ff]" />;
    if (trend.direction === 'down') return <TrendingDown className="h-4 w-4 text-[#ff025f]" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-zinc-400 text-xs mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="performance-trends">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'athletic' ? 'default' : 'outline'}
            onClick={() => setViewMode('athletic')}
            size="sm"
            className="text-xs"
          >
            <Activity className="h-3 w-3 mr-1" />
            Athletic
          </Button>
          <Button
            variant={viewMode === 'psychological' ? 'default' : 'outline'}
            onClick={() => setViewMode('psychological')}
            size="sm"
            className="text-xs"
          >
            <Brain className="h-3 w-3 mr-1" />
            Psychological
          </Button>
          <Button
            variant={viewMode === 'comparison' ? 'default' : 'outline'}
            onClick={() => setViewMode('comparison')}
            size="sm"
            className="text-xs"
          >
            <Target className="h-3 w-3 mr-1" />
            Compare
          </Button>
        </div>

        {viewMode === 'athletic' && (
          <Select value={selectedTest} onValueChange={setSelectedTest}>
            <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700 text-sm">
              <SelectValue placeholder="Select test type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">All Tests (Avg Grade)</SelectItem>
              {testTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="3m">Last 3 Months</SelectItem>
            <SelectItem value="6m">Last 6 Months</SelectItem>
            <SelectItem value="1y">Last Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Trend Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-xs uppercase tracking-wide">Athletic Performance</span>
            <TrendIcon trend={athleticTrend} />
          </div>
          <div className="text-2xl font-bold text-white">
            {athleticChartData.length > 0 
              ? (selectedTest === 'all' 
                  ? athleticChartData[athleticChartData.length - 1]?.avgGrade?.toFixed(1) 
                  : athleticChartData[athleticChartData.length - 1]?.grade)
              : '-'
            }
            <span className="text-sm text-zinc-500 ml-1">/5</span>
          </div>
          <p className={`text-xs mt-1 ${athleticTrend.direction === 'up' ? 'text-[#02b5ff]' : athleticTrend.direction === 'down' ? 'text-[#ff025f]' : 'text-zinc-500'}`}>
            {athleticTrend.change > 0 ? '+' : ''}{athleticTrend.change}% vs previous
          </p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-xs uppercase tracking-wide">Psychological</span>
            <TrendIcon trend={psychTrend} />
          </div>
          <div className="text-2xl font-bold text-white">
            {ratingsChartData.length > 0 ? ratingsChartData[ratingsChartData.length - 1]?.psychological?.toFixed(1) : '-'}
            <span className="text-sm text-zinc-500 ml-1">/10</span>
          </div>
          <p className={`text-xs mt-1 ${psychTrend.direction === 'up' ? 'text-[#02b5ff]' : psychTrend.direction === 'down' ? 'text-[#ff025f]' : 'text-zinc-500'}`}>
            {psychTrend.change > 0 ? '+' : ''}{psychTrend.change}% vs previous
          </p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-400 text-xs uppercase tracking-wide">Social</span>
            <TrendIcon trend={socialTrend} />
          </div>
          <div className="text-2xl font-bold text-white">
            {ratingsChartData.length > 0 ? ratingsChartData[ratingsChartData.length - 1]?.social?.toFixed(1) : '-'}
            <span className="text-sm text-zinc-500 ml-1">/10</span>
          </div>
          <p className={`text-xs mt-1 ${socialTrend.direction === 'up' ? 'text-[#02b5ff]' : socialTrend.direction === 'down' ? 'text-[#ff025f]' : 'text-zinc-500'}`}>
            {socialTrend.change > 0 ? '+' : ''}{socialTrend.change}% vs previous
          </p>
        </div>
      </div>

      {/* Charts */}
      {viewMode === 'athletic' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {selectedTest === 'all' ? 'Average Grade Over Time' : `${selectedTest} Performance`}
          </h3>
          
          {athleticChartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-zinc-500">
              No athletic test data available
            </div>
          ) : (
            <SafeResponsiveContainer width="100%" height={300} minHeight={300}>
              {selectedTest === 'all' ? (
                <AreaChart data={athleticChartData}>
                  <defs>
                    <linearGradient id="gradeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 5]} stroke="#666" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="avgGrade" 
                    name="Avg Grade"
                    stroke="#3B82F6" 
                    fill="url(#gradeGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              ) : (
                <LineChart data={athleticChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" stroke="#666" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 5]} stroke="#10B981" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="value" 
                    name={`Result (${athleticChartData[0]?.unit || ''})`}
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', r: 4 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="grade" 
                    name="Grade"
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ fill: '#10B981', r: 4 }}
                  />
                </LineChart>
              )}
            </SafeResponsiveContainer>
          )}
        </div>
      )}

      {viewMode === 'psychological' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Trend Line Chart */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Rating Trends Over Time</h3>
            {ratingsChartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-zinc-500">
                No coach rating data available
              </div>
            ) : (
              <SafeResponsiveContainer width="100%" height={300} minHeight={300}>
                <LineChart data={ratingsChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} stroke="#666" tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="psychological" name="Psychological" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="social" name="Social" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="overall" name="Overall" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </SafeResponsiveContainer>
            )}
          </div>

          {/* Radar Chart */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Latest Rating Profile</h3>
            {latestRatingRadar.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-zinc-500">
                No coach rating data available
              </div>
            ) : (
              <SafeResponsiveContainer width="100%" height={300} minHeight={300}>
                <RadarChart data={latestRatingRadar}>
                  <PolarGrid stroke="#333" />
                  <PolarAngleAxis dataKey="trait" tick={{ fill: '#999', fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#666', fontSize: 10 }} />
                  <Radar name="Rating" dataKey="value" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                </RadarChart>
              </SafeResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {viewMode === 'comparison' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Performance Comparison</h3>
          
          {/* Combined view showing athletic grades and coach ratings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Test Performance by Type */}
            <div>
              <h4 className="text-sm font-medium text-zinc-400 mb-3">Athletic Tests by Category</h4>
              {testTypes.length === 0 ? (
                <div className="text-zinc-500 text-sm">No test data</div>
              ) : (
                <div className="space-y-3">
                  {testTypes.map(testType => {
                    const testsOfType = athleticTests.filter(t => t.test_name === testType);
                    const latestTest = testsOfType.sort((a, b) => new Date(b.test_date) - new Date(a.test_date))[0];
                    const avgGrade = testsOfType.reduce((sum, t) => sum + t.grade, 0) / testsOfType.length;
                    
                    return (
                      <div key={testType} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-white">{testType}</p>
                          <p className="text-xs text-zinc-500">{testsOfType.length} tests recorded</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <span 
                              className="px-2 py-1 rounded text-xs font-bold"
                              style={{ 
                                backgroundColor: `${GRADE_COLORS[latestTest?.grade]}20`,
                                color: GRADE_COLORS[latestTest?.grade]
                              }}
                            >
                              Grade {latestTest?.grade}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 mt-1">Avg: {avgGrade.toFixed(1)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Coach Rating Categories */}
            <div>
              <h4 className="text-sm font-medium text-zinc-400 mb-3">Coach Rating Categories</h4>
              {coachRatings.length === 0 ? (
                <div className="text-zinc-500 text-sm">No rating data</div>
              ) : (
                <div className="space-y-3">
                  {['Confidence', 'Commitment', 'Mental Control', 'Concentration', 'Enjoying Training', 'Helping Others', 'Communication', 'Involvement'].map(trait => {
                    const fieldKey = trait === 'Mental Control' ? 'mental_control' : 
                                     trait === 'Enjoying Training' ? 'enjoying_training' :
                                     trait === 'Helping Others' ? 'helping_others' :
                                     trait.toLowerCase();
                    const latest = coachRatings[0][fieldKey];
                    const avg = coachRatings.reduce((sum, r) => sum + (r[fieldKey] || 0), 0) / coachRatings.length;
                    
                    const bgColor = latest >= 8 ? 'bg-[#02b5ff]/20' : latest >= 5 ? 'bg-[#02b5ff]/20' : 'bg-[#ff025f]/20';
                    const textColor = latest >= 8 ? 'text-[#02b5ff]' : latest >= 5 ? 'text-[#02b5ff]' : 'text-[#ff025f]';
                    
                    return (
                      <div key={trait} className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
                        <span className="text-sm text-zinc-300">{trait}</span>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}>
                            {latest}/10
                          </span>
                          <span className="text-xs text-zinc-500">avg {avg.toFixed(1)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Grade Legend */}
      <div className="flex flex-wrap gap-4 justify-center text-xs">
        {Object.entries(GRADE_COLORS).reverse().map(([grade, color]) => (
          <div key={grade} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
            <span className="text-zinc-400">Grade {grade}: {GRADE_LABELS[grade]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
